import React, { useEffect, useState, useMemo } from 'react'
import { BillboardButton, useOverlay, InfoSheetPanel, Paragraph, Separator } from '@fs/zion-ui'
import { ContentAdd } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import zionDebug from '@fs/zion-debug'
import ZionForm, { useZionForm } from '@fs/zion-form'
import { yupResolver } from '@hookform/resolvers/yup'
import cloneDeep from 'lodash.clonedeep'
import * as Yup from 'yup'
import {
  generateSls,
  getImage,
  getNestedOrFloatingElementById,
  getPersonName,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useR2DispatchContext, useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import Tip from '../../../../components/tip/Tip'
import PanelHeader from './PanelHeader'
import FieldSelectorOverlay from '../../../../components/generic/panels/shared/fieldChipSelector/FieldSelectorOverlay'
import { translateAdditionalFieldChips } from '../../../../utils/translationUtils'
import { addPlaceAndDateFieldsToFormDefaults, getNextUuid } from '../../../../../shared/utils/utils'
import EditPersonFormElement from './EditPersonFormElement'
import SaveComponent from '../../../../components/generic/panels/shared/SaveComponent'
import {
  handleOnFocus,
  removeFieldFromValues,
  submitEditFields,
  getCreateDispatchesForSingleField,
} from './utils/editPanelFieldHelpers'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import FieldWrapper from '../ui/FieldWrapper'
import RowWrapper from '../ui/RowWrapper'
import useSetRecordHighlightsOfOneRecord from '../../hooks/useSetRecordHighlightsOfOneRecord'
import { useCollectionContextProvider } from '../../../../providers/collectionProvider/CollectionProvider'
import { FIELDS } from '../../../../providers/collectionProvider/collectionConstants'
import useUnsavedChangesOverlay from '../../../../hooks/useUnsavedChangesOverlay'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { R2AppName } from '../../../../../shared/services/config'

const debug = zionDebug('rip:R2:EditOtherFields')

function EditOtherFields() {
  const state = useSlsStateContext()
  const r2State = useR2StateContext()
  const r2Dispatch = useR2DispatchContext()
  const { clearEdits, setDisplayHighlights, getDispatches } = useSlsHighlightsContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const [t] = useTranslation()
  const additionalFieldChipData = translateAdditionalFieldChips(state.types)
  const additionalFieldSelectorOverlay = useOverlay()
  const [otherFields, setOtherFields] = useState([])
  const setRecordHighlightsOfOneRecord = useSetRecordHighlightsOfOneRecord()
  const { editOtherFieldsOverlay, editHighlightsOverlay, closeOverlay, topMostPanel, inPanelStack } =
    useOverlayContext()
  const saveChangesOverlay = useOverlay()

  const shape = {}
  const schema = Yup.object().shape(shape)
  const methods = useZionForm({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {},
  })

  const { restrictedFields } = useCollectionContextProvider()
  const isRestricted = restrictedFields?.includes(FIELDS.OTHER)

  const currentImage = getImage(state?.images, state.currentImageArk)
  const selectedPerson = getNestedOrFloatingElementById([currentImage], r2State?.selectedPersonId)
  const otherFieldData = useMemo(() => {
    return selectedPerson?.subElements?.filter(
      (se) =>
        (!se.subElements || se.elementType === 'FIELD') &&
        se.fieldType !== 'SEX_CODE' &&
        se.fieldType !== 'RACE_OR_COLOR' &&
        se.elementType !== 'AGE' &&
        se.typeData?.displayable &&
        se.typeData?.editable
    ) // All other non structured fields
  }, [selectedPerson])

  const { setValue, reset } = methods
  const heading = t('edit-other-fields.heading', 'Additional Fields')
  const instructions = t(
    'edit-other-fields.instructions',
    'Fix any fields that are incorrect including their highlights. Add or remove fields as necessary.'
  )
  const addMoreFields = t('edit-index.add-custom.button', 'Add or Remove {field}', { field: heading })

  editOtherFieldsOverlay.onClose = () => {
    if (methods.formState.isDirty) {
      methods.reset()
      setOtherFields(cloneDeep(otherFieldData))
    }
  }

  useEffect(() => {
    const formDefaultValues = {}
    if (otherFieldData) addPlaceAndDateFieldsToFormDefaults(formDefaultValues, otherFieldData)
    reset(formDefaultValues)
  }, [otherFieldData, reset])

  useEffect(() => {
    if (editOtherFieldsOverlay.isOpen && topMostPanel() === 'editOtherFields' && !editHighlightsOverlay.isOpen) {
      const otherFieldsTypes = state.types.fields
        .filter((field) => field.elementTypes?.includes('PERSON') && field.fieldType !== 'RACE_OR_COLOR')
        .map((field) => field.fieldType)
      const splitScreenImageArk = thumbnails?.[r2State.referenceFocusedIndex]?.ark
      const elementIds = [r2State.selectedPersonId]
      setDisplayHighlights({
        splitScreenPartitionKey: splitScreenImageArk,
        mainPartitionKey: state.currentImageArk,
        fieldTypes: otherFieldsTypes,
        elementIds,
      })
      setRecordHighlightsOfOneRecord()
    }
  }, [
    setDisplayHighlights,
    editHighlightsOverlay.isOpen,
    editOtherFieldsOverlay.isOpen,
    r2State.editHighlights,
    r2State.referenceEditHighlights,
    r2State.referenceFocusedIndex,
    r2State.selectedPersonId,
    r2State.splitScreen,
    setRecordHighlightsOfOneRecord,
    state.currentImageArk,
    thumbnails,
    state.types.fields,
    topMostPanel,
  ])

  useUnsavedChangesOverlay({
    debug,
    debugMessage: 'Unsaved changes overlay called: ',
    methods,
    overlay: saveChangesOverlay,
  })

  const resetAndClose = () => {
    clearEdits()
    r2Dispatch({ type: 'REVERT_HIGHLIGHTS', value: {} })
    closeOverlay('editOtherFields')
  }

  // The back arrow on the panel was clicked
  const userClosedPanel = () => {
    if (methods.formState.isDirty) saveChangesOverlay.open()
    else {
      resetAndClose()
    }
  }

  useEffect(() => {
    setOtherFields(cloneDeep(otherFieldData))
  }, [otherFieldData])

  const handleChipSelection = (chips, lockedChips) => {
    const outFieldsThatStayed = (field) =>
      !chips.map(({ id }) => id).includes(field.id) && !lockedChips.map(({ id }) => id).includes(field.id)
    const newFields = [...otherFields]

    const removeField = (field) => {
      removeFieldFromValues(field, methods)
      const index = newFields.findIndex((f) => f.id === field.id)
      newFields.splice(index, 1)
    }
    otherFields.filter(outFieldsThatStayed).forEach(removeField)

    const newChips = chips.filter((chip) => !chip.id)
    if (newChips) {
      newChips.reverse() // Chips are added at the front of the array, so reversing them here gets back to the order they were clicked
      newChips.forEach((chip) => {
        const newField = {
          label: chip.label,
          fieldType: chip.value.split(':')[0],
          id: getNextUuid(),
          added: true,
        }
        newFields.push(newField)
        setValue(newField.id, '')
        setValue(`${newField.id}_fieldType`, chip.value.split(':')[0])
      })
    }

    setOtherFields(newFields)
  }

  const handleSubmit = (values) => {
    const dispatches = submitEditFields({
      ...state,
      ...r2State,
      values,
      formState: methods.formState,
      inPanelStack,
      currentImageArk: state.currentImageArk,
    }).concat(
      getDispatches({
        mainImageArk: state.currentImageArk,
        splitScreenImageArk: thumbnails?.[r2State.referenceFocusedIndex]?.ark,
      })
    )
    return { sls: generateSls(dispatches), dispatches }
  }

  /**
   * This useEffect prepares dispatches to create a field in SLS when:
   * 1) the highlight being selected for the field is in use by one or more other fields
   * 2) the editSharedHighlightPage2 panel comes into view
   * 3) the field does not yet exist in SLS
   * The dispatches are saved in R2 state and use in editSharedHighlightPage2.js when preparing and submitting dispatches
   */
  useEffect(() => {
    const existingField = getNestedOrFloatingElementById(state.images, r2State.highlightsFieldInfo.id)
    if (topMostPanel() === 'editSharedHighlightPage2' && r2State.editDispatches.length === 0 && !existingField) {
      const dispatchesForHighlightField = getCreateDispatchesForSingleField({
        ...state,
        ...r2State,
        inPanelStack,
        values: methods.getValues(),
        formState: methods.formState,
        currentImageArk: state.currentImageArk,
      })
      r2Dispatch({
        type: 'SET_EDIT_DISPATCHES',
        value: dispatchesForHighlightField,
      })
    }
  }, [inPanelStack, methods, r2Dispatch, r2State, state, topMostPanel])

  const finished = () => {
    closeOverlay('editOtherFields')
  }

  debug('Render: ', {
    otherFields,
    otherFieldData,
    selectedPerson,
    isDirty: methods.formState.isDirty,
    values: methods.getValues(),
  })

  return (
    <ZionForm onSubmit={handleSubmit} methods={methods} id="edit-other-fields">
      <InfoSheetPanel
        {...{ ...editOtherFieldsOverlay, close: userClosedPanel }}
        headingColor="green"
        title={t('edit-index.edit-person.heading', 'Edit {personName}', {
          personName: getPersonName({ person: selectedPerson }),
        })}
        footer={
          <SaveComponent
            saveChangesOverlay={saveChangesOverlay}
            onSubmit={handleSubmit}
            finished={finished}
            canceled={resetAndClose}
            appName={R2AppName}
            personId={r2State.userId}
            formName="edit-other-fields"
            saveInitiatorFilename="EditOtherFields.js"
          />
        }
      >
        <PanelHeader heading={heading} caption={instructions} headingSize="H5" />
        <Tip
          useOverline={false}
          primaryText={t('edit-index.person-details.additional-fields-tip-title', 'Help with Additional Fields')}
          listPrimaryText={
            <>
              <span>
                <b>
                  {t('edit-index.person-details.additional-fields-tip-title-1', '1. What is in Additional Fields?')}
                </b>
              </span>
              <Separator size="xxs" />
              <Paragraph size="sm">
                {t(
                  'edit-index.person-details.additional-fields-tip-title-1-text',
                  'Additional fields are any remaining information on the document that doesn’t fit in any other category.'
                )}
              </Paragraph>
              <Separator size="sm" />
              <span>
                <b>{t('edit-index.person-details.additional-fields-tip-title-2', '2. Why is this section so long?')}</b>
              </span>
              <Separator size="xxs" />
              <Paragraph size="sm">
                {t(
                  'edit-index.person-details.additional-fields-tip-title-2-text',
                  'The AI transcription may have picked up on a lot more information then the essential Name, Event and Relationships. So this section is the catch all for the rest of the information.'
                )}
              </Paragraph>
            </>
          }
        />
        <RowWrapper>
          {otherFields
            ?.filter((field) => {
              // We were told to hide these fields.  Eventually SLS may send us type data telling us to disable it
              return field?.fieldType !== 'MISC_IMAGE_FOLDER_1950_CENSUS' && field?.fieldType !== 'IMAGE_DIMENSIONS'
            })
            .map((field) => (
              <FieldWrapper key={field.id}>
                <EditPersonFormElement
                  field={field}
                  handleOnFocus={() => handleOnFocus({ r2Dispatch, element: field })}
                  handleFieldChange={() => r2Dispatch({ type: 'SET_ACTIVE_FIELD_ID', value: '' })}
                />
              </FieldWrapper>
            ))}
          <BillboardButton
            data-testid="add-or-remove-fields"
            Icon={ContentAdd}
            color="yellow"
            emphasis="low"
            disabled={isRestricted}
            onClick={additionalFieldSelectorOverlay.handleClick}
          >
            {addMoreFields}
          </BillboardButton>
        </RowWrapper>
        <FieldSelectorOverlay
          title={addMoreFields}
          disabled={isRestricted}
          chipData={[
            {
              title: t('edit-index.chips.additional-fields.title', 'Additional Fields'),
              data: additionalFieldChipData,
              key: 'additionalFields',
            },
          ]}
          fieldData={otherFields}
          handleSelection={handleChipSelection}
          overlay={additionalFieldSelectorOverlay}
          allowSelectingMultiple={false}
        />
      </InfoSheetPanel>
    </ZionForm>
  )
}

export default EditOtherFields
